/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import _ from 'lodash';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { css as cuicss, placeholder } from '@coursera/coursera-ui';

import type { ObjectiveListQuery_EnterpriseTargetSkillProfileSummariesV1Resource_byProgram_elements_targetSkillProficiencies as TargetSkillProficiency } from 'bundles/program-home/components/__generated__/ObjectiveListQuery';

import _t from 'i18n!nls/program-home';

type SkillPillListProp = {
  targetSkillProficiencies: TargetSkillProficiency[];
};

const styles = {
  skillPillList: (theme: Theme) => css`
    display: flex;
    flex-wrap: wrap;
    margin: ${theme.spacing(8)} 0;
    height: 68px;
    padding: 0;
  `,
  skillPillContainer: (theme: Theme) => css`
    max-width: calc(50% - ${theme.spacing(8)});
    margin-right: ${theme.spacing(8)};
    display: flex;
    height: 26px;
  `,
  lastPill: css`
    max-width: 50%;
    margin-right: 0;
  `,
  itemText: (theme: Theme) => css`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    background: ${theme.palette.gray[100]};
    border-radius: 2px;
    padding: 1px ${theme.spacing(8)};
  `,
  moreText: (theme: Theme) => css`
    padding: 1px 0;
    margin-left: ${theme.spacing(8)};
    flex-shrink: 0;
  `,
};

export function SkillPillList({ targetSkillProficiencies }: SkillPillListProp) {
  const theme = useTheme();

  const skillListLength = targetSkillProficiencies.length;
  return (
    <ul css={styles.skillPillList(theme)}>
      {targetSkillProficiencies.slice(0, 4).map((skill, index) => {
        const { skillName, skillId } = skill;
        return (
          <li key={skillId} css={[styles.skillPillContainer(theme), index === 3 && styles.lastPill]}>
            <Typography2 component="p" css={styles.itemText(theme)}>
              {skillName}
            </Typography2>
            {index === 3 && skillListLength > 4 && (
              <Typography2 component="p" css={styles.moreText(theme)}>
                <FormattedMessage message={_t(' + {numSkills} more')} numSkills={skillListLength - 4} />
              </Typography2>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function SkillPillListPlaceholder() {
  const theme = useTheme();
  const placeholderStyles = {
    placeholderItemText: css`
      width: 100px;
    `,
  };
  return (
    <ul css={styles.skillPillList(theme)} aria-hidden>
      {_.times(4, (value) => (
        <li key={value} css={[styles.skillPillContainer(theme)]} aria-hidden>
          <Typography2 component="p" css={styles.itemText(theme)} aria-hidden>
            <div aria-hidden {...cuicss(placeholder.styles.shimmer)} css={placeholderStyles.placeholderItemText}>
              &nbsp;
            </div>
          </Typography2>
        </li>
      ))}
    </ul>
  );
}
